import React, { useEffect, useState, useMemo } from "react";
import {
  getAllPointsGames,
  deletePointsGame,
} from "../../services/pointsGameService";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";
import { useNavigate } from "react-router-dom";
import { FaTrash, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";

export const GamesPointsListLayer = () => {
  const navigate = useNavigate();

  const [games, setGames] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);

  const [filterLeague, setFilterLeague] = useState("");
  const [filterMatch, setFilterMatch] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [gamesRes, matchRes, leagueRes] =
        await Promise.all([
          getAllPointsGames(),
          getMatches(),
          getLeagues(),
        ]);

      if (gamesRes?.status)
        setGames(gamesRes.data.data || []);

      if (matchRes?.status)
        setMatches(matchRes.data || []);

      if (leagueRes?.status)
        setLeagues(leagueRes.data || []);
    } catch {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  /* ===== League Based Match ===== */
  const leagueMatches = useMemo(() => {
    if (!filterLeague) return matches;
    return matches.filter(
      (m) => String(m.league_id) === String(filterLeague)
    );
  }, [filterLeague, matches]);

  /* ===== FILTER ===== */
  const filtered = useMemo(() => {
    return games.filter((g) => {
      const match = matches.find(
        (m) => String(m.id) === String(g.match_id)
      );

      return (
        (!filterLeague ||
          String(match?.league_id) === String(filterLeague)) &&
        (!filterMatch ||
          String(g.match_id) === String(filterMatch)) &&
        (!search ||
          g.question.toLowerCase().includes(search.toLowerCase()))
      );
    });
  }, [games, matches, filterLeague, filterMatch, search]);

  /* ===== PAGINATION ===== */
  const totalPages = Math.ceil(filtered.length / pageSize);

  const paginated = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const getMatchName = (id) => {
    const match = matches.find(
      (m) => String(m.id) === String(id)
    );
    return match
      ? `${match.team_a} vs ${match.team_b}`
      : "-";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this game?")) return;

    const res = await deletePointsGame(id);
    if (res?.status) {
      toast.success("Deleted Successfully");
      loadData();
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="card shadow-sm">

        {/* HEADER */}
        <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <h4 className="fw-bold mb-0">
            Points Games List
          </h4>
          <button
            className="btn btn-primary btn-sm"
            onClick={() =>
              navigate("/add-games-points")
            }
          >
            <FaPlus className="me-2" />
            Add Game
          </button>
        </div>

        <div className="card-body">

          {/* FILTER BAR */}
          <div className="row g-2 mb-3">

            <div className="col-12 col-md-3">
              <select
                className="form-select"
                value={filterLeague}
                onChange={(e) => {
                  setFilterLeague(e.target.value);
                  setFilterMatch("");
                  setPage(1);
                }}
              >
                <option value="">
                  All Leagues
                </option>
                {leagues.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.league_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-3">
              <select
                className="form-select"
                value={filterMatch}
                onChange={(e) => {
                  setFilterMatch(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">
                  All Matches
                </option>
                {leagueMatches.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.team_a} vs {m.team_b}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-3">
              <input
                className="form-control"
                placeholder="Search Question..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-12 col-md-3">
              <select
                className="form-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>Show 10</option>
                <option value={50}>Show 50</option>
                <option value={100}>Show 100</option>
              </select>
            </div>

          </div>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Match</th>
                  <th>Question</th>
                  <th>Entry</th>
                  <th>Admin %</th>
                  <th>Winner %</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      Loading...
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No Data Found
                    </td>
                  </tr>
                ) : (
                  paginated.map((g, i) => (
                    <tr key={g.id}>
                      <td>
                        {(page - 1) * pageSize + i + 1}
                      </td>
                      <td>
                        {getMatchName(g.match_id)}
                      </td>
                      <td>{g.question}</td>
                      <td>{g.entry_amount}</td>
                      <td>{g.admin_percentage}%</td>
                      <td>
                        {g.user_distribution_percentage}%
                      </td>
                      <td>{g.status}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            handleDelete(g.id)
                          }
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3">
              <ul className="pagination mb-0">

                <li
                  className={`page-item ${
                    page === 1 ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setPage(page - 1)
                    }
                  >
                    Prev
                  </button>
                </li>

                {[...Array(totalPages)].map(
                  (_, i) => (
                    <li
                      key={i}
                      className={`page-item ${
                        page === i + 1
                          ? "active"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          setPage(i + 1)
                        }
                      >
                        {i + 1}
                      </button>
                    </li>
                  )
                )}

                <li
                  className={`page-item ${
                    page === totalPages
                      ? "disabled"
                      : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setPage(page + 1)
                    }
                  >
                    Next
                  </button>
                </li>

              </ul>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};